import { useCallback } from 'react'

import { resetEditingState, useCrudSelection } from '@/atoms'
import { addTrafficProfile } from '@/features/domain/traffic'
import { composeRecurrenceId } from '@/server-data'
import { useAppDispatch } from '@/store'

import type { TrafficProfileFormValues } from '../types'
import { isAlreadyExistingRecurrence } from '../utils/isAlreadyExistingRecurrence'
import { useRecurrencesMap } from './useRecurrencesMap'

export function useOnSubmit() {
  const recurrenceMap = useRecurrencesMap()
  const [, setTrafficProfileSelection] = useCrudSelection('trafficProfile')
  const dispatch = useAppDispatch()

  const onCreateRecurrence = useCallback(
    async (recurrence: uui.domain.rm.Recurrence) => {
      const thunkResult = await dispatch(addTrafficProfile(recurrence))
      if (!addTrafficProfile.fulfilled.match(thunkResult)) {
        throw new Error(thunkResult.payload?.message ?? 'Internal error')
      }

      resetEditingState()
      setTrafficProfileSelection([thunkResult.payload])
    },
    [dispatch, setTrafficProfileSelection],
  )

  const onOpenExistingRecurrence = useCallback(
    (recurrence: uui.domain.rm.Recurrence) => {
      const recurrenceId = composeRecurrenceId(recurrence.type, recurrence.value)

      resetEditingState()
      setTrafficProfileSelection([recurrenceId])
    },
    [setTrafficProfileSelection],
  )

  return useCallback(
    (formValues: TrafficProfileFormValues) => {
      if (!formValues.recurrence) {
        throw new Error('Recurrence is undefined')
      }
      isAlreadyExistingRecurrence(recurrenceMap, formValues.recurrence)
        ? onOpenExistingRecurrence(formValues.recurrence)
        : onCreateRecurrence(formValues.recurrence)
    },
    [recurrenceMap],
  )
}
